import { config } from '@iheartjane/config';
import { useQuery } from '@tanstack/react-query';

import type { Coordinates } from '@jane/search/types';
import { api } from '@jane/shared/data-access';
/* eslint-disable-next-line no-restricted-imports */
import type { Preferences } from '@jane/shared/models';
import { encodeQuery } from '@jane/shared/util';

interface ApiParams {
  distance: Preferences['storeSearchRadius'];
  fulfillment_type: Preferences['storeFulfillmentType'];
  lat?: Coordinates['lat'];
  long?: Coordinates['long'];
  open_now?: boolean;
  store_type: Preferences['storeType'];
  zipcode?: string;
}

interface ApiResponse {
  store_ids: number[];
}

export const fetchUserPreferredStoreIds = async ({
  coordinates,
  zipcode,
  userPreferences = {},
}: {
  coordinates?: Coordinates;
  userPreferences: Preferences;
  zipcode?: string;
}): Promise<ApiResponse['store_ids']> => {
  const params: ApiParams = {
    ...coordinates,
    zipcode,
    distance: userPreferences.storeSearchRadius,
    fulfillment_type: userPreferences.storeFulfillmentType,
    store_type: userPreferences.storeType,
  };

  if (userPreferences.storeAvailability === 'openNow') {
    params.open_now = true;
  }

  const url = encodeQuery(
    `${config.apiPath}/stores/ids_by_shopping_preferences`,
    params
  );

  const res: ApiResponse = await api.get(url);

  return res.store_ids;
};

export const useUserPreferredStoreIds = ({
  coordinates,
  zipcode,
  userPreferences = {},
}: {
  coordinates?: Coordinates;
  userPreferences?: Preferences;
  zipcode?: string;
}) =>
  useQuery({
    enabled: !!coordinates?.lat || !!coordinates?.long || !!zipcode,
    queryFn: () =>
      fetchUserPreferredStoreIds({ coordinates, zipcode, userPreferences }),
    queryKey: ['userPreferredStores', coordinates, userPreferences, zipcode],
  });
